import { getJValue, getQmax, getPWF } from "./productivityIndex";

export function getVogelPreCalcs(
  reservoirPressurePSIA: number,
  wellFlowingPressureTestPSIA: number,
  totalLiquidFlowRateSTBD: number,
  bubblePointPressure: number,
  waterCutRatio: number,
  useTotalLiquidRate = false
) {
  // THESE CALCULATIONS ARE IDENTICAL ON BOTH SHEETS

  // Qtest
  const q_test = totalLiquidFlowRateSTBD;
  // Pr
  const p_r = reservoirPressurePSIA;
  // Pwf Test
  const p_wf_test = wellFlowingPressureTestPSIA;
  // Pb
  const p_b = bubblePointPressure;
  // Oil Test
  const q_o_test = q_test * (1 - waterCutRatio);
  // Water Test
  const q_w_test = q_test * waterCutRatio;

  // These are only for modified vogel
  // Jwater
  const j_water = useTotalLiquidRate ? 0 : q_w_test / (p_r - p_wf_test);
  // Joil
  const j_oil = useTotalLiquidRate
    ? 0
    : getJValue(p_wf_test, p_b, q_o_test, p_r);

  // This is for normal (total rate) vogel
  // Jtotal
  const j_total = useTotalLiquidRate
    ? getJValue(p_wf_test, p_b, q_test, p_r)
    : 0;

  // qb
  // Use j_total if using total liquid rate
  const q_b =
    p_b > p_r
      ? 0
      : useTotalLiquidRate
        ? j_total * (p_r - p_b)
        : j_oil * (p_r - p_b);

  // Use j_total and q_test if using total rate
  const q_max = useTotalLiquidRate
    ? getQmax(p_r, p_b, q_test, p_wf_test, q_b, j_total)
    : getQmax(p_r, p_b, q_o_test, p_wf_test, q_b, j_oil);

  return {
    q_o_test,
    q_w_test,
    q_test,
    p_r,
    p_wf_test,
    p_b,
    j_water,
    j_oil,
    j_total,
    q_b,
    q_max
  };
}

export function getMaxFlowRateVogelModified(
  reservoirPressurePSIA: number,
  wellFlowingPressureTestPSIA: number,
  desiredWellFlowingPressurePSIA: number,
  totalLiquidFlowRateSTBD: number,
  bubblePointPressure: number,
  waterCutRatio: number
) {
  // 'Solve for Q'
  const { p_r, p_b, j_water, j_oil, q_b, q_max } = getVogelPreCalcs(
    reservoirPressurePSIA,
    wellFlowingPressureTestPSIA,
    totalLiquidFlowRateSTBD,
    bubblePointPressure,
    waterCutRatio
  );

  // THIS CALCULATION IS AT THE BOTTOM OF THE SPREADSHEET
  const p_wf = desiredWellFlowingPressurePSIA;

  const q_w = j_water * (p_r - p_wf);

  let q_o = 0;

  if (p_wf < p_b && p_b > p_r) {
    q_o =
      q_b + (q_max - q_b) * (1 - 0.2 * (p_wf / p_r) - 0.8 * (p_wf / p_r) ** 2);
  } else if (p_wf < p_b) {
    q_o =
      q_b + (q_max - q_b) * (1 - 0.2 * (p_wf / p_b) - 0.8 * (p_wf / p_b) ** 2);
  } else {
    q_o = j_oil * (p_r - p_wf);
  }

  const q = q_w + q_o;

  return q;
}

// https://github.com/user-attachments/files/17050011/Vogel.-.Total.Flow.IPR.xlsx
export function getMaxFlowRateVogel(
  reservoirPressurePSIA: number,
  wellFlowingPressureTestPSIA: number,
  desiredWellFlowingPressurePSIA: number,
  totalLiquidFlowRateSTBD: number,
  bubblePointPressure: number,
  waterCutRatio: number
) {
  // Perform initial calculations using VogelPreCalcs
  // q_max is D24
  const { p_r, p_b, q_b, q_max, j_total } = getVogelPreCalcs(
    reservoirPressurePSIA,
    wellFlowingPressureTestPSIA,
    totalLiquidFlowRateSTBD,
    bubblePointPressure,
    waterCutRatio,
    true
  );

  const p_wf = desiredWellFlowingPressurePSIA;

  let q_t = 0;

  if (p_wf < p_b && p_b > p_r) {
    q_t =
      q_b + (q_max - q_b) * (1 - 0.2 * (p_wf / p_r) - 0.8 * (p_wf / p_r) ** 2);
  } else if (p_wf < p_b) {
    q_t =
      q_b + (q_max - q_b) * (1 - 0.2 * (p_wf / p_b) - 0.8 * (p_wf / p_b) ** 2);
  } else {
    q_t = j_total * (p_r - p_wf);
  }

  return q_t;
}

type VogelModifiedCheckAndIterationRowData = {
  initialWaterTargetSTBD: number;
  initialWaterCut: number;
  initialOilTargetSTBD: number;
  initialPwfDatumPSIA: number;
  checkWaterTargetSTBD: number;
  checkWaterCut: number;
  checkOilTargetSTBD: number;
  checkPwfDatumPSIA: number;
  pwfDiff: number | null;
};

// implemented from formulas at static/Vogel - Modified 1.xlsx
// TODO - at some point this should be refactored into it's own file
// and use the context paradigm we have with other functions as well
// as more tests added for it
export function getWellFlowingPressureUsingVogelModifiedRate(
  reservoirPressurePSIA: number,
  wellFlowingPressureTestPSIA: number,
  wellTestLiquidFlowRateSTBD: number,
  targetLiquidFlowRateSTBD: number,
  bubblePointPressurePSIA: number,
  waterCutRatio: number
) {
  // Column F
  const { q_b, q_max, j_oil, j_water } = getVogelPreCalcs(
    reservoirPressurePSIA,
    wellFlowingPressureTestPSIA,
    wellTestLiquidFlowRateSTBD,
    bubblePointPressurePSIA,
    waterCutRatio
  );

  // Column I
  const q_w_max = reservoirPressurePSIA * j_water;
  const q_max_total = q_w_max + q_max;
  // Fix from Vogel - Modified pwf bug.xlsx
  const waterCutAtJ = Math.min(1, j_water / (j_oil + j_water));
  const waterCutAtQMax = Math.min(1, q_w_max / q_max_total);

  // O20-R20
  const initialWaterTargetSTBD = targetLiquidFlowRateSTBD * waterCutAtJ;
  const initialOilTargetSTBD =
    targetLiquidFlowRateSTBD - initialWaterTargetSTBD;
  const initialPwfDatumPSIA = getPWF(
    waterCutRatio,
    reservoirPressurePSIA,
    targetLiquidFlowRateSTBD,
    initialOilTargetSTBD,
    q_b,
    bubblePointPressurePSIA,
    q_max,
    j_oil,
    j_water
  );
  // N21-R21
  const checkWaterCut = waterCutAtQMax;
  const checkWaterTargetSTBD = targetLiquidFlowRateSTBD * checkWaterCut;
  const checkOilTargetSTBD = targetLiquidFlowRateSTBD - checkWaterTargetSTBD;
  const checkPwfDatumPSIA = getPWF(
    waterCutRatio,
    reservoirPressurePSIA,
    targetLiquidFlowRateSTBD,
    checkOilTargetSTBD,
    q_b,
    bubblePointPressurePSIA,
    q_max,
    j_oil,
    j_water
  );
  const pwfDiff = null;

  const initialResults: VogelModifiedCheckAndIterationRowData = {
    initialWaterCut: waterCutAtJ,
    initialWaterTargetSTBD,
    initialOilTargetSTBD,
    initialPwfDatumPSIA,
    checkWaterCut: waterCutAtQMax,
    checkWaterTargetSTBD,
    checkOilTargetSTBD,
    checkPwfDatumPSIA,
    pwfDiff
  };

  return doVogelModifiedOuterIterations(
    initialResults,
    targetLiquidFlowRateSTBD,
    q_max,
    waterCutRatio,
    reservoirPressurePSIA,
    bubblePointPressurePSIA,
    j_water,
    j_oil,
    q_b,
    waterCutAtQMax
  );
}

// Remaining row values for N6-R6 to N146-R146
export function doVogelModifiedOuterIterations(
  initialResults: VogelModifiedCheckAndIterationRowData,
  targetLiquidFlowRateSTBD: number,
  q_max: number,
  waterCutRatio: number,
  reservoirPressurePSIA: number,
  bubblePointPressurePSIA: number,
  j_water: number,
  j_oil: number,
  q_b: number,
  waterCutAtMax: number
): number {
  let avgBestPwfDatumPSIAResult = 0;
  let iter = 0;

  let bestResults = initialResults;
  while (iter < 3) {
    // N23-R23 to N2024-R2024
    // W23-AA23 to W2024-AA2024
    // AF23-AJ23 to AF220-AJ220
    bestResults = getVogelModifiedBestResultsFromColumn(
      bestResults,
      targetLiquidFlowRateSTBD,
      q_max,
      waterCutRatio,
      reservoirPressurePSIA,
      bubblePointPressurePSIA,
      j_water,
      j_oil,
      q_b,
      waterCutAtMax,
      1000
    );
    iter++;
  }
  // I15 (AI225)
  const avgOilRateAtTargetSTBD =
    (bestResults.checkOilTargetSTBD + bestResults.initialOilTargetSTBD) / 2;
  // I17
  avgBestPwfDatumPSIAResult = getPWF(
    waterCutRatio,
    reservoirPressurePSIA,
    targetLiquidFlowRateSTBD,
    avgOilRateAtTargetSTBD,
    q_b,
    bubblePointPressurePSIA,
    q_max,
    j_oil,
    j_water
  );
  return avgBestPwfDatumPSIAResult;
}

// N23-R23 to N2024-R2024
// W23-AA23 to W2024-AA2024
// AF23-AJ23 to AF220-AJ220
export function getVogelModifiedBestResultsFromColumn(
  bestResults: VogelModifiedCheckAndIterationRowData,
  targetLiquidFlowRateSTBD: number,
  q_max: number,
  waterCutRatio: number,
  reservoirPressurePSIA: number,
  bubblePointPressurePSIA: number,
  j_water: number,
  j_oil: number,
  q_b: number,
  waterCutAtMax: number,
  numTries: number
): VogelModifiedCheckAndIterationRowData {
  let iter = 0;
  const results = [];

  while (iter <= numTries) {
    results.push(
      getVogelModifiedRowData(
        bestResults.initialWaterCut,
        bestResults.checkWaterCut,
        targetLiquidFlowRateSTBD,
        q_max,
        waterCutRatio,
        reservoirPressurePSIA,
        bubblePointPressurePSIA,
        j_water,
        j_oil,
        q_b,
        waterCutAtMax,
        numTries,
        iter
      )
    );
    iter++;
  }
  // Skip over nulls
  const bestDiffSet = results.reduce((prev, curr) => {
    if (prev.pwfDiff === null && curr.pwfDiff === null) {
      return curr;
    } else if (curr.pwfDiff === null) {
      return prev;
    } else if (prev.pwfDiff === null) {
      return curr;
    } else {
      return prev.pwfDiff < curr.pwfDiff ? prev : curr;
    }
  });
  return bestDiffSet;
}

export function getVogelModifiedRowData(
  bestInitialWaterCut: number,
  bestCheckWaterCut: number,
  targetLiquidFlowRateSTBD: number,
  q_max: number,
  waterCutRatio: number,
  reservoirPressurePSIA: number,
  bubblePointPressurePSIA: number,
  j_water: number,
  j_oil: number,
  q_b: number,
  waterCutAtMax: number,
  numTries: number,
  iter: number
): VogelModifiedCheckAndIterationRowData {
  const initialWaterCut =
    Math.min(bestCheckWaterCut, bestInitialWaterCut) +
    (iter * Math.abs(bestInitialWaterCut - bestCheckWaterCut)) / numTries;
  const initialWaterTargetSTBD = targetLiquidFlowRateSTBD * initialWaterCut;
  const initialOilTargetSTBD =
    targetLiquidFlowRateSTBD - initialWaterTargetSTBD;
  const initialPwfDatumPSIA = getPWF(
    waterCutRatio,
    reservoirPressurePSIA,
    targetLiquidFlowRateSTBD,
    initialOilTargetSTBD,
    q_b,
    bubblePointPressurePSIA,
    q_max,
    j_oil,
    j_water
  );
  const checkWaterTargetSTBD =
    (reservoirPressurePSIA - initialPwfDatumPSIA) * j_water;
  const checkWaterCut =
    targetLiquidFlowRateSTBD === 0
      ? 0
      : checkWaterTargetSTBD / targetLiquidFlowRateSTBD;
  const checkOilTargetSTBD =
    targetLiquidFlowRateSTBD - checkWaterTargetSTBD > q_max
      ? q_max
      : Math.max(targetLiquidFlowRateSTBD - checkWaterTargetSTBD, 0);
  const checkPwfDatumPSIA = getPWF(
    waterCutRatio,
    reservoirPressurePSIA,
    targetLiquidFlowRateSTBD,
    checkOilTargetSTBD,
    q_b,
    bubblePointPressurePSIA,
    q_max,
    j_oil,
    j_water
  );
  // Return null diff if wc exceeds max
  const pwfDiff =
    checkWaterCut > waterCutAtMax
      ? null
      : Math.abs(checkPwfDatumPSIA - initialPwfDatumPSIA);
  return {
    initialWaterCut,
    initialWaterTargetSTBD,
    initialOilTargetSTBD,
    initialPwfDatumPSIA,
    checkWaterTargetSTBD,
    checkWaterCut,
    checkOilTargetSTBD,
    checkPwfDatumPSIA,
    pwfDiff
  };
}

// https://github.com/user-attachments/files/17050011/Vogel.-.Total.Flow.IPR.xlsx
export function getWellFlowingPressureUsingVogelRate(
  // Pr
  reservoirPressurePSIA: number,
  // pwf
  wellFlowingPressureTestPSIA: number,
  totalLiquidFlowRateSTBD: number,
  desiredLiquidFlowRateSTBD: number,
  // Pb
  bubblePointPressure: number,
  // water cut
  waterCutRatio: number
) {
  const { p_r, p_b, q_b, q_max, j_total } = getVogelPreCalcs(
    reservoirPressurePSIA,
    wellFlowingPressureTestPSIA,
    totalLiquidFlowRateSTBD,
    bubblePointPressure,
    waterCutRatio,
    true
  );

  if (desiredLiquidFlowRateSTBD < q_b) {
    return p_r - desiredLiquidFlowRateSTBD / j_total;
  } else if (desiredLiquidFlowRateSTBD >= q_b && p_r >= p_b) {
    return (
      0.125 *
      p_b *
      (-1 +
        Math.sqrt(
          81 - 80 * ((desiredLiquidFlowRateSTBD - q_b) / (q_max - q_b))
        ))
    );
  } else {
    // (desiredLiquidFlowRateSTBD >= q_b && p_r < p_b)
    return (
      0.125 *
      p_r *
      (-1 +
        Math.sqrt(
          81 - 80 * ((desiredLiquidFlowRateSTBD - q_b) / (q_max - q_b))
        ))
    );
  }
}
